import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';

import { convertToArray } from 'Details/utils';
import { addQueryParams, getStateFromLocation } from 'utils/query-string';
import isEqual from 'lodash/isEqual';

import { QueryStringContext } from './';

class Provider extends React.Component {
	static propTypes = {
		children: PropTypes.node.isRequired,
		history: PropTypes.object.isRequired,
		location: PropTypes.object.isRequired,
		defaultCurrency: PropTypes.string.isRequired,
		defaultCurrencyType: PropTypes.string.isRequired,
		defaultUnit: PropTypes.string.isRequired,
		defaultDateRangeId: PropTypes.string,
		defaultDateFrom: PropTypes.string,
		defaultDateTo: PropTypes.string,
		dateRanges: PropTypes.arrayOf(PropTypes.object),
	};

	state = {
		showHarmonizedVendors: 'true',
		showNegativeSpend: 'false',
		spendBy: 'category',
		splitSpendByDimension: '0',
		indirectTaxonomy: 1,
		directTaxonomy: 1,
		currency: this.props.defaultCurrency,
		currencyType: this.props.defaultCurrencyType,
		unit: this.props.defaultUnit,
		dateRangeId: this.props.defaultDateRangeId,
		dateFrom: this.props.defaultDateFrom,
		dateTo: this.props.defaultDateTo,
		showNonSourceable: 'false',
		setQueryStringState: state => this.setState(state),
		setQueryStringStateAndPush: state => this.setQueryParams(state, 'push'),
		setPathnameWithQueryStringStateAndPush: (pathname, state) =>
			this.setPathnameWithQueryParams(pathname, state, 'push'),
		setQueryStringStateAndReplace: state =>
			this.setQueryParams(state, 'replace'),
	};

	historyUnsubscribe = null;

	componentDidUpdate(prevProps) {
		const {
			defaultDateRangeId,
			defaultDateFrom,
			defaultDateTo,
			defaultCurrency,
			defaultDateRanges,
		} = this.props;
		const { currency, setQueryStringStateAndPush } = this.state;

		if (currency !== defaultCurrency) {
			setQueryStringStateAndPush({ currency: defaultCurrency });
		}

		if (
			prevProps.defaultDateRangeId !== defaultDateRangeId ||
			prevProps.defaultDateFrom !== defaultDateFrom ||
			prevProps.defaultDateTo !== defaultDateTo ||
			prevProps.defaultCurrency !== defaultCurrency ||
			!isEqual(prevProps.defaultDateRanges, defaultDateRanges)
		) {
			this.setState({
				currency: defaultCurrency,
				dateRangeId: defaultDateRangeId,
				dateFrom: defaultDateFrom,
				dateTo: defaultDateTo,
			});
		}
	}

	componentWillMount() {
		const { history, location } = this.props;
		// Add history listener
		this.historyUnsubscribe = history.listen(nextLocation => {
			this.setStateFromLocation(nextLocation);
		});
		// Set initial state from location
		this.setStateFromLocation(location);
	}

	componentWillUnmount() {
		// Remove history listener
		this.historyUnsubscribe();
	}

	setStateFromLocation = location => {
		const locationState = location.state || {};
		const queryStringState = getStateFromLocation(location);
		let newState = {
			...locationState,
			...queryStringState,
		};

		if (newState && newState.customFilters && newState.customFiltersValues) {
			const {
				customFilters,
				customFiltersValues,
			} = this.getSortedCustomFilters(
				convertToArray(newState.customFilters),
				convertToArray(newState.customFiltersValues)
			);

			newState = {
				...newState,
				customFilters,
				customFiltersValues,
			};
		}

		this.setState(newState);
	};

	setQueryParams = (state, action) => {
		const { history, location } = this.props;

		const nextLocation = addQueryParams(location, state);
		history[action](nextLocation);
	};

	getSortedCustomFilters = (customFilters, customFiltersValues) => {
		const filters = [];
		const values = [];

		const filterKeys = Array.from(new Set([...customFilters]));

		filterKeys.sort();

		filterKeys.forEach(filterKey => {
			const filteredValues = customFiltersValues.filter(
				(_0, i) => customFilters[i] === filterKey
			);

			filteredValues.sort();

			filteredValues.forEach(value => {
				filters.push(filterKey);
				values.push(value);
			});
		});

		return {
			customFilters: filters,
			customFiltersValues: values,
		};
	};

	setPathnameWithQueryParams = (pathname, state, action) => {
		const { history, location } = this.props;

		const nextLocation = addQueryParams(location, state);
		nextLocation.pathname = pathname;

		history[action](nextLocation);
	};

	render() {
		return (
			<QueryStringContext.Provider value={this.state}>
				{this.props.children}
			</QueryStringContext.Provider>
		);
	}
}

export const QueryStringContextProvider = withRouter(Provider);
